import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUserSpaces } from '../hooks/useClientData';
import { useAddOpForm } from '../hooks/useAddOpForm';
import { TABS } from '../lib/constants';

// frontend-restructuring Fase 3 (03.1-auth-store.md §6 grupo 1) -- presentacional, `user`
// llega por prop de su contenedor (ClientsView.jsx) en vez de leer `useUser()` directo.
// Fase 5.15 -- `react-hook-form` + `zodResolver` + la mutación viven en `useAddOpForm`.
// Fase 5.18 (05-remediation.md, parte 2) -- `react-hooks/set-state-in-effect` (hint de React
// Compiler, no un bug de runtime) sube a `error` en todo `src/`. Este archivo hace setState
// síncrono dentro de un `useEffect` en un patrón de sincronización legítimo: preselecciona el space activo del usuario en el selector del modal (documentado como intencional en el propio archivo).
// No se reescribe a estado derivado (repetiría el fetch/haría el UI menos predecible en
// varios de estos casos) -- deshabilitado a nivel de archivo, con motivo, no en silencio.
/* eslint-disable react-hooks/set-state-in-effect */
export default function AddOpModal({ onClose, onSave, user }) {
  const { t } = useTranslation('client');
  // Selector explícito de space (space-default-onboarding.md, decisión #2): opcional, con
  // fallback al space activo del usuario -- no es un campo de formulario validado, es un
  // selector con default, se queda fuera de `addOpForm`.
  const [spaceId, setSpaceId] = useState(user?.currentSpace?.id ?? null);
  const { data: userSpaces = [] } = useUserSpaces(user?.dbId);

  useEffect(() => {
    if (user?.currentSpace?.id) setSpaceId(user.currentSpace.id);
  }, [user?.currentSpace?.id]);

  // 02.3-client.md paso b′ -- validación con react-hook-form + zodResolver (error visible sólo
  // tras el primer intento fallido de submit, vía `formState.isSubmitted`). Fase 5.15: la
  // instancia del form vive en `useAddOpForm`.
  const { register, formState: { errors, isSubmitted }, submitOp } = useAddOpForm({ onSave, onClose });

  return (
    <div className="client-modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="client-modal">
        <h3>{t('addModal.title')}</h3>
        <input className="client-modal-input" placeholder={t('addModal.transactionTitlePlaceholder')} aria-label={t('addModal.transactionTitleAriaLabel')}
          aria-invalid={isSubmitted && !!errors.transaction}
          {...register('transaction')} />
        {isSubmitted && errors.transaction && (
          <div role="alert" style={{ fontSize: '10px', color: '#f87171', padding: '0 var(--space-1) var(--space-1)' }}>
            {t('addModal.transactionRequired')}
          </div>
        )}
        <div className="client-modal-row">
          <select className="client-modal-select" aria-label={t('addModal.categoryAriaLabel')} {...register('category')}>
            {TABS.map(tab => <option key={tab.id} value={tab.id}>{tab.label}</option>)}
          </select>
          <select className="client-modal-select" aria-label={t('addModal.statusAriaLabel')} {...register('status')}>
            {['PLANEADO','EN CURSO','COMPLETADO','BLOQUEADO','PAUSA'].map(s =>
              <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        {userSpaces.length > 0 && (
          <div className="client-modal-row">
            <select className="client-modal-select" aria-label={t('addModal.spaceAriaLabel')} value={spaceId ?? ''} onChange={e => setSpaceId(e.target.value ? Number(e.target.value) : null)}>
              {userSpaces.map(s => (
                <option key={s.id} value={s.id}>
                  {s.id === user?.currentSpace?.id ? t('addModal.spaceDefaultOption', { name: s.name }) : s.name}
                </option>
              ))}
            </select>
          </div>
        )}
        <div className="client-modal-row">
          <input className="client-modal-input" placeholder={t('addModal.totalAmountPlaceholder')} aria-label={t('addModal.totalAmountAriaLabel')} type="number"
            {...register('amount')} />
          <select className="client-modal-select" aria-label={t('addModal.currencyAriaLabel')} {...register('currency')}>
            <option value="MXN">MXN</option>
            <option value="USD">USD</option>
          </select>
        </div>
        <div className="client-modal-row">
          <input className="client-modal-input" placeholder={t('addModal.costPlaceholder')} aria-label={t('addModal.costAriaLabel')} type="number"
            {...register('cost')} />
          <input className="client-modal-input" placeholder={t('addModal.salePricePlaceholder')} aria-label={t('addModal.salePriceAriaLabel')} type="number"
            {...register('price_amount')} />
        </div>
        <div className="client-modal-row">
          <input className="client-modal-input" placeholder={t('addModal.operationTimePlaceholder')} aria-label={t('addModal.operationTimeAriaLabel')}
            {...register('operation_time')} />
          <input className="client-modal-input" placeholder={t('addModal.priceTextPlaceholder')} aria-label={t('addModal.priceTextAriaLabel')}
            {...register('price')} />
        </div>
        <div className="client-modal-actions">
          <button className="client-modal-cancel" onClick={onClose}>{t('addModal.cancel')}</button>
          <button className="client-modal-submit" onClick={submitOp(spaceId)}>{t('addModal.save')}</button>
        </div>
      </div>
    </div>
  );
}
